import type {
  ChecklistState,
  CustomerStatusInput,
  DnsStatusInput,
  IntegrationStatusInput,
  KnowledgeStatusInput,
  OnboardingItem,
  OnboardingReadiness,
  SslStatusInput,
  TenantHealthInput,
} from "./types";
import { doctorToChecklistState, mapDoctorLabel } from "./doctor";
import { dnsDetailWithEvidence, sslDetailWithEvidence } from "./evidence";
import { mapIntegrationStatus } from "./integration";

/** Requirements that must all be verified for Production Ready (Phase 5). */
export const PRODUCTION_READY_REQUIREMENTS = [
  { id: "runtime-doctor", label: "Runtime Doctor" },
  { id: "ai-provider", label: "AI" },
  { id: "email", label: "Email" },
  { id: "shopify", label: "Shopify" },
  { id: "dns", label: "DNS" },
  { id: "ssl", label: "SSL" },
  { id: "knowledge-base", label: "Knowledge Base" },
] as const;

function integrationById(
  integrations: IntegrationStatusInput[] | null | undefined,
  id: string,
): IntegrationStatusInput | undefined {
  return integrations?.find((i) => i.id === id);
}

function dnsItem(dns?: DnsStatusInput | null): OnboardingItem {
  if (!dns) {
    return {
      id: "dns",
      label: "DNS configured",
      state: "missing",
      detail: "DNS not tested yet — run Test DNS",
    };
  }
  if (dns.state === "verified" && dns.ok) {
    return {
      id: "dns",
      label: "DNS configured",
      state: "complete",
      detail: dns.last_message || "DNS verified",
    };
  }
  if (dns.state === "failed") {
    return {
      id: "dns",
      label: "DNS configured",
      state: "warning",
      detail: dnsDetailWithEvidence(dns),
    };
  }
  return {
    id: "dns",
    label: "DNS configured",
    state: "missing",
    detail: dns.last_message || "DNS not configured / not tested",
  };
}

function sslItem(ssl?: SslStatusInput | null): OnboardingItem {
  if (!ssl) {
    return {
      id: "ssl",
      label: "SSL active",
      state: "missing",
      detail: "SSL not tested yet — run Test SSL",
    };
  }
  if (ssl.state === "verified" && ssl.ok) {
    return {
      id: "ssl",
      label: "SSL active",
      state: "complete",
      detail: ssl.last_message || "SSL verified",
    };
  }
  if (ssl.state === "expiring_soon") {
    return {
      id: "ssl",
      label: "SSL active",
      state: "warning",
      detail: sslDetailWithEvidence(ssl),
    };
  }
  if (ssl.state === "failed") {
    return {
      id: "ssl",
      label: "SSL active",
      state: "warning",
      detail: sslDetailWithEvidence(ssl),
    };
  }
  return {
    id: "ssl",
    label: "SSL active",
    state: "missing",
    detail: ssl.last_message || "SSL not configured / not tested",
  };
}

function knowledgeItem(knowledge?: KnowledgeStatusInput | null): OnboardingItem {
  if (!knowledge) {
    return {
      id: "knowledge-base",
      label: "Knowledge Base ready",
      state: "missing",
      detail: "Knowledge status not loaded — open Configure to verify",
    };
  }
  if (knowledge.ready) {
    return {
      id: "knowledge-base",
      label: "Knowledge Base ready",
      state: "complete",
      detail: knowledge.detail,
    };
  }
  if (
    (knowledge.chunks_indexed != null && knowledge.chunks_indexed > 0 && !knowledge.ready) ||
    knowledge.pdf_count > 0
  ) {
    return {
      id: "knowledge-base",
      label: "Knowledge Base ready",
      state: "warning",
      detail: knowledge.detail,
    };
  }
  return {
    id: "knowledge-base",
    label: "Knowledge Base ready",
    state: "missing",
    detail: knowledge.detail,
  };
}

/**
 * Derive onboarding checklist ONLY from verified registry / platform / integration fields.
 * Production Ready uses PRODUCTION_READY_REQUIREMENTS — Ops must use this same function.
 */
export function deriveOnboarding(
  customer: CustomerStatusInput,
  tenantHealth: TenantHealthInput | null,
  integrations?: IntegrationStatusInput[] | null,
  knowledge?: KnowledgeStatusInput | null,
  dns?: DnsStatusInput | null,
  ssl?: SslStatusInput | null,
): OnboardingItem[] {
  const hasStack = tenantHealth?.has_stack === true;
  const doctor = tenantHealth?.doctor_status;
  const provisioned = Boolean(customer.provision_completed_at);

  const openai = integrationById(integrations, "openai");
  const email = integrationById(integrations, "email");
  const shopify = integrationById(integrations, "shopify");

  const doctorItem = (): OnboardingItem => {
    const state = doctorToChecklistState(doctor);
    return {
      id: "runtime-doctor",
      label: "Runtime Doctor passed",
      state,
      detail:
        state === "missing"
          ? "No verified doctor scan for this tenant"
          : state === "complete"
            ? "Last scan reported pass"
            : state === "warning"
              ? `Last scan reported ${doctor === "fail" ? "failures" : "warnings"}`
              : "Doctor status unknown",
    };
  };

  const integrationItem = (
    id: string,
    label: string,
    row: IntegrationStatusInput | undefined,
  ): OnboardingItem => {
    if (integrations == null) {
      return {
        id,
        label,
        state: "unknown",
        detail: "Integration status not loaded",
      };
    }
    if (row?.connected) {
      return {
        id,
        label,
        state: "complete",
        detail: row.last_message || row.last_verification || "Verified connection test passed",
      };
    }
    const status = mapIntegrationStatus(row);
    return {
      id,
      label,
      state: "missing",
      detail:
        status === "Configured"
          ? "Credentials saved — Connected requires a successful connection test"
          : "Not configured — no verified connection test",
    };
  };

  const itemsWithoutProduction: OnboardingItem[] = [
    {
      id: "tenant-created",
      label: "Tenant created",
      state: "complete",
      detail: `Registry entry ${customer.slug}`,
    },
    {
      id: "secrets",
      label: "Secrets generated",
      state: hasStack ? "complete" : tenantHealth ? "missing" : "unknown",
      detail: hasStack
        ? "Tenant stack .env present on host"
        : tenantHealth
          ? "No stack .env detected"
          : "Stack presence not verified",
    },
    {
      id: "database",
      label: "Database initialized",
      state: provisioned ? "complete" : customer.status === "pending" ? "missing" : "unknown",
      detail: provisioned
        ? "Provision completed timestamp recorded"
        : customer.status === "pending"
          ? "Provisioning has not completed"
          : "No verified database initialization signal",
    },
    dnsItem(dns),
    sslItem(ssl),
    integrationItem("ai-provider", "AI provider configured", openai),
    integrationItem("email", "Email configured", email),
    integrationItem("shopify", "Shopify configured", shopify),
    knowledgeItem(knowledge),
    doctorItem(),
  ];

  const productionReady = (): OnboardingItem => {
    const byId = Object.fromEntries(itemsWithoutProduction.map((i) => [i.id, i]));
    const failing: string[] = [];
    for (const req of PRODUCTION_READY_REQUIREMENTS) {
      const row = byId[req.id];
      if (!row || row.state !== "complete") {
        failing.push(req.label);
      }
    }
    if (customer.status !== "active") {
      failing.push("Tenant active");
    }
    if (!hasStack) {
      failing.push("Stack present");
    }

    if (failing.length === 0) {
      return {
        id: "production-ready",
        label: "Production Ready",
        state: "complete",
        detail:
          "Verified: Runtime Doctor, AI, Email, Shopify, DNS, SSL, Knowledge Base",
      };
    }

    const unique = [...new Set(failing)];
    return {
      id: "production-ready",
      label: "Production Ready",
      state: "missing",
      detail: `Not ready — missing/failed: ${unique.join(", ")}`,
    };
  };

  return [...itemsWithoutProduction, productionReady()];
}

export function deriveOnboardingReadiness(
  customer: CustomerStatusInput,
  tenantHealth: TenantHealthInput | null,
  integrations?: IntegrationStatusInput[] | null,
  knowledge?: KnowledgeStatusInput | null,
  dns?: DnsStatusInput | null,
  ssl?: SslStatusInput | null,
): OnboardingReadiness {
  const items = deriveOnboarding(customer, tenantHealth, integrations, knowledge, dns, ssl);
  const productionReady = items.find((i) => i.id === "production-ready");

  if (productionReady?.state === "complete") {
    return "Ready";
  }

  if (customer.status === "failed") {
    return "Needs Attention";
  }
  if (items.some((i) => i.state === "warning")) {
    return "Needs Attention";
  }

  return "In Progress";
}

export function checklistLabel(state: ChecklistState): string {
  if (state === "complete") return "Complete";
  if (state === "warning") return "Warning";
  if (state === "missing") return "Missing";
  return "Unknown";
}

export type ChecklistToneKey = "success" | "warning" | "danger" | "neutral";

export function checklistToneKey(state: ChecklistState): ChecklistToneKey {
  if (state === "complete") return "success";
  if (state === "warning") return "warning";
  if (state === "missing") return "danger";
  return "neutral";
}

/** Doctor display label for deployment / badges — not checklist Complete/Missing. */
export { mapDoctorLabel };
